import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { RegulationJourneySummary } from '../utils/buildProductRegulationsJourneyByLaw'

export const REGULATION_JOURNEY_PROGRESS_CIRCLE_SIZE = 20

const SIZE = REGULATION_JOURNEY_PROGRESS_CIRCLE_SIZE
const STROKE_WIDTH = 2
const RADIUS = (SIZE - STROKE_WIDTH) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

type RegulationJourneyProgressInput = {
  journey: RegulationJourneySummary | undefined
  isLoading: boolean
}

export function getRegulationJourneyProgressDetailLabel(
  t: TFunction<'common'>,
  input: RegulationJourneyProgressInput
): string {
  if (input.isLoading) {
    return t('v2.pages.products.detail.regulationsSummary.journeyLoading')
  }
  if (input.journey == null) {
    return t(
      'v2.pages.products.detail.regulationsSummary.journeyInactiveNoTracking'
    )
  }
  if (input.journey.isComplete) {
    return t('v2.pages.products.detail.regulationsSummary.journeyComplete')
  }
  return t('v2.pages.products.detail.regulationsSummary.journeySteps', {
    complete: input.journey.completeCount,
    total: input.journey.totalCount,
  })
}

export function getRegulationJourneyProgressShortLabel(
  t: TFunction<'common'>,
  input: RegulationJourneyProgressInput
): string | null {
  if (input.isLoading) {
    return t('v2.pages.products.detail.regulationsSummary.journeyLoadingShort')
  }
  if (input.journey == null) {
    return null
  }
  if (input.journey.isComplete) {
    return t('v2.pages.products.detail.regulationsSummary.journeyCompleteShort')
  }
  return t('v2.pages.products.detail.regulationsSummary.journeyStepsShort', {
    complete: input.journey.completeCount,
    total: input.journey.totalCount,
  })
}

type RegulationJourneyProgressCircleProps = RegulationJourneyProgressInput

export function RegulationJourneyProgressCircle({
  journey,
  isLoading,
}: RegulationJourneyProgressCircleProps) {
  const { t } = useTranslation('common')
  const progressLabel = getRegulationJourneyProgressDetailLabel(t, {
    journey,
    isLoading,
  })
  const showProgress = journey != null && !isLoading
  const progressRatio =
    showProgress && journey.totalCount > 0
      ? journey.completeCount / journey.totalCount
      : 0
  const dashOffset = CIRCUMFERENCE * (1 - progressRatio)
  const isComplete = showProgress && journey.isComplete
  const showInactiveRing = journey == null && !isLoading

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <span
          className='relative mt-0.5 inline-flex shrink-0'
          style={{ width: SIZE, height: SIZE }}
          aria-hidden
        >
          <svg
            width={SIZE}
            height={SIZE}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            className='-rotate-90'
            aria-hidden
          >
            {showInactiveRing ? (
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill='none'
                strokeWidth={STROKE_WIDTH}
                className='stroke-muted-foreground/35'
              />
            ) : (
              <>
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  fill='none'
                  strokeWidth={STROKE_WIDTH}
                  className='stroke-muted-foreground/25'
                />
                {showProgress && !isComplete ? (
                  <circle
                    cx={SIZE / 2}
                    cy={SIZE / 2}
                    r={RADIUS}
                    fill='none'
                    strokeWidth={STROKE_WIDTH}
                    strokeLinecap='round'
                    strokeDasharray={CIRCUMFERENCE}
                    strokeDashoffset={dashOffset}
                    className='stroke-primary transition-[stroke-dashoffset] duration-300'
                  />
                ) : null}
                {isComplete ? (
                  <circle
                    cx={SIZE / 2}
                    cy={SIZE / 2}
                    r={RADIUS}
                    fill='none'
                    strokeWidth={STROKE_WIDTH}
                    className='stroke-green-600 dark:stroke-green-500'
                  />
                ) : null}
              </>
            )}
          </svg>
          {isLoading ? (
            <Loader2
              className='absolute inset-0 m-auto size-2.5 animate-spin text-muted-foreground'
              aria-hidden
            />
          ) : null}
        </span>
      </TooltipTrigger>
      <TooltipContent side='top' className='max-w-sm'>
        {progressLabel}
      </TooltipContent>
    </Tooltip>
  )
}
